import { 
  GOOGLE_CLIENT_ID, 
  GOOGLE_API_KEY, 
  DISCOVERY_DOC, 
  BACKUP_FILE_NAME, 
  SCOPE 
} from '../utils/constants';

class GoogleDriveService {
  constructor() {
    this.isInitialized = false;
    this.isSignedIn = false;
    this.tokenClient = null;
    this.accessToken = null;
    this.userInfo = null;
    this.initPromise = null;
  }

  /**
   * Validate configuration
   */
  _validateConfiguration() {
    const hasValidKeys = GOOGLE_CLIENT_ID && 
                        GOOGLE_API_KEY && 
                        !GOOGLE_CLIENT_ID.includes('placeholder') &&
                        !GOOGLE_API_KEY.includes('placeholder') &&
                        GOOGLE_CLIENT_ID.length > 20;

    if (!hasValidKeys) {
      console.warn('⚠️ Configuration Google Drive invalide');
      return false;
    }

    return true;
  }

  /**
   * Initialize Google APIs with Google Identity Services
   */
  async initializeGapi() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  async _doInitialize() {
    if (this.isInitialized) {
      console.log('✅ Google API already initialized');
      return;
    }

    try {
      if (!this._validateConfiguration()) {
        throw new Error('Configuration Google Drive invalide');
      }

      console.log('🔄 Loading Google API scripts...');
      
      // Charger Google API
      await this._loadScriptWithRetry('https://apis.google.com/js/api.js');
      
      // Charger Google Identity Services
      await this._loadScriptWithRetry('https://accounts.google.com/gsi/client');
      
      console.log('✅ Scripts loaded successfully');

      // Attendre que les APIs soient disponibles
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!window.gapi) {
        throw new Error('Google API not available');
      }

      if (!window.google?.accounts?.oauth2) {
        throw new Error('Google Identity Services not available');
      }

      // Initialiser GAPI client
      await new Promise((resolve, reject) => {
        window.gapi.load('client', {
          callback: resolve,
          onerror: () => reject(new Error('Failed to load GAPI client'))
        });
      });
      
      await window.gapi.client.init({
        apiKey: GOOGLE_API_KEY,
        discoveryDocs: [DISCOVERY_DOC]
      });

      console.log('✅ Google API initialized successfully');
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Error initializing Google API:', error);
      this.isInitialized = false;
      throw new Error(`Impossible d'initialiser Google Drive: ${error.message}`);
    }
  }

  /**
   * Load script with retry
   */
  async _loadScriptWithRetry(src, maxRetries = 2) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this._loadScript(src);
        return;
      } catch (error) {
        console.warn(`Script load attempt ${attempt} failed:`, error);
        if (attempt === maxRetries) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  /**
   * Load external script
   */
  _loadScript(src) {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      
      const timer = setTimeout(() => {
        reject(new Error(`Script timeout: ${src}`));
      }, 10000);

      script.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      
      script.onerror = () => {
        clearTimeout(timer);
        reject(new Error(`Failed to load: ${src}`));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Connect using Google Identity Services
   */
  async connect() {
    if (!this.isInitialized) {
      await this.initializeGapi();
    }

    return new Promise((resolve, reject) => {
      try {
        // Initialiser le token client
        this.tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPE,
          callback: async (tokenResponse) => {
            if (tokenResponse.error) {
              console.error('❌ OAuth error:', tokenResponse);
              reject(new Error(this._getErrorMessage(tokenResponse.error)));
              return;
            }

            if (tokenResponse.access_token) {
              this.accessToken = tokenResponse.access_token;
              this.isSignedIn = true;
              
              try {
                await this._loadUserInfo();
                console.log('✅ Successfully connected to Google Drive');
                resolve({
                  userInfo: this.userInfo
                });
              } catch (userError) {
                console.error('❌ Error loading user info:', userError);
                // Continue anyway with basic info
                this.userInfo = {
                  name: 'Utilisateur Google',
                  email: 'user@gmail.com',
                  picture: '',
                  getName: () => 'Utilisateur Google',
                  getEmail: () => 'user@gmail.com',
                  getImageUrl: () => ''
                };
                resolve({
                  userInfo: this.userInfo
                });
              }
            }
          },
          error_callback: (error) => {
            console.error('❌ Token client error:', error);
            reject(new Error('Erreur lors de l\'authentification Google'));
          }
        });

        // Demander l'autorisation
        this.tokenClient.requestAccessToken({ 
          prompt: 'select_account'
        });

      } catch (error) {
        console.error('❌ Connection error:', error);
        reject(new Error('Erreur lors de la configuration de l\'authentification'));
      }
    });
  }

  /**
   * Get user information
   */
  async _loadUserInfo() {
    if (!this.accessToken) {
      throw new Error('No access token available');
    }

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const userData = await response.json();
      
      this.userInfo = {
        name: userData.name || 'Utilisateur Google',
        email: userData.email || 'user@gmail.com',
        picture: userData.picture || '',
        getName: () => userData.name || 'Utilisateur Google',
        getEmail: () => userData.email || 'user@gmail.com',
        getImageUrl: () => userData.picture || ''
      };
    } catch (error) {
      console.error('❌ Error fetching user info:', error);
      throw error;
    }
  }

  /**
   * Get user-friendly error messages
   */
  _getErrorMessage(errorCode) {
    const errorMessages = {
      'popup_closed_by_user': 'Connexion annulée par l\'utilisateur',
      'access_denied': 'Accès refusé. Veuillez autoriser l\'accès à Google Drive.',
      'popup_blocked_by_browser': 'Popup bloqué. Autorisez les popups pour ce site.',
      'immediate_failed': 'Connexion automatique échouée'
    };
    
    return errorMessages[errorCode] || `Erreur de connexion: ${errorCode}`;
  }

  /**
   * Disconnect
   */
  async disconnect() {
    try {
      if (this.accessToken && window.google?.accounts?.oauth2) {
        window.google.accounts.oauth2.revoke(this.accessToken);
      }
      
      this.accessToken = null;
      this.tokenClient = null;
      this.userInfo = null;
      this.isSignedIn = false;
      
      console.log('✅ Disconnected from Google Drive');
    } catch (error) {
      console.error('❌ Error disconnecting:', error);
      // Force cleanup
      this.accessToken = null;
      this.tokenClient = null;
      this.userInfo = null;
      this.isSignedIn = false;
      throw new Error('Erreur lors de la déconnexion');
    }
  }

  /**
   * Find backup file
   */
  async findBackupFile() {
    if (!this.isSignedIn || !this.accessToken) {
      throw new Error('Non connecté à Google Drive');
    }

    try {
      const response = await window.gapi.client.drive.files.list({
        q: `name='${BACKUP_FILE_NAME}' and trashed=false`,
        spaces: 'drive',
        fields: 'files(id, name, modifiedTime, size)'
      });
      
      const files = response.result.files;
      return files && files.length > 0 ? files[0] : null;
    } catch (error) {
      console.error('❌ Error finding backup file:', error);
      throw new Error('Erreur lors de la recherche du fichier');
    }
  }

  /**
   * Upload data
   */
  async uploadData(data) {
    if (!this.isSignedIn || !this.accessToken) {
      throw new Error('Non connecté à Google Drive');
    }

    try {
      const existingFile = await this.findBackupFile();
      const fileContent = JSON.stringify(data, null, 2);
      
      const boundary = '-------314159265358979323846';
      const delimiter = '\r\n--' + boundary + '\r\n';
      const close_delim = '\r\n--' + boundary + '--';

      const metadata = {
        name: BACKUP_FILE_NAME
      };

      let body = delimiter +
        'Content-Type: application/json\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: application/json\r\n\r\n' +
        fileContent +
        close_delim;

      const method = existingFile ? 'PATCH' : 'POST';
      const url = existingFile 
        ? `https://www.googleapis.com/upload/drive/v3/files/${existingFile.id}?uploadType=multipart`
        : 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': `multipart/related; boundary="${boundary}"`
        },
        body: body
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Data uploaded successfully');
      return result;

    } catch (error) {
      console.error('❌ Error uploading data:', error);
      throw new Error(`Erreur lors de la sauvegarde: ${error.message}`);
    }
  }

  /**
   * Download data
   */
  async downloadData() {
    if (!this.isSignedIn || !this.accessToken) {
      throw new Error('Non connecté à Google Drive');
    }

    try {
      const file = await this.findBackupFile();
      if (!file) {
        console.log('ℹ️ No backup file found');
        return null;
      }

      const response = await window.gapi.client.drive.files.get({
        fileId: file.id,
        alt: 'media'
      });

      if (response.status !== 200) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const data = JSON.parse(response.body);
      console.log('✅ Data downloaded successfully');
      return data;

    } catch (error) {
      console.error('❌ Error downloading data:', error);
      throw new Error(`Erreur lors du chargement: ${error.message}`);
    }
  }

  /**
   * Check connection status
   */
  isConnected() {
    return this.isInitialized && this.isSignedIn && !!this.accessToken && !!this.userInfo;
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.userInfo;
  }

  /**
   * Get status
   */
  getStatus() {
    return {
      isConnected: this.isConnected(),
      isInitialized: this.isInitialized,
      isSignedIn: this.isSignedIn,
      userInfo: this.userInfo,
      hasValidConfig: this._validateConfiguration()
    };
  }

  /**
   * Test connection
   */
  async testConnection() {
    if (!this.isConnected()) {
      return false;
    }

    try {
      await window.gapi.client.drive.files.list({
        pageSize: 1,
        fields: 'files(id, name)'
      });
      return true;
    } catch (error) {
      console.error('❌ Connection test failed:', error);
      return false;
    }
  }
}

// Export singleton
export const googleDriveService = new GoogleDriveService();
export default googleDriveService;